/**
 * IndxOne Hub — Tier limitations enforcement
 * 
 * Centralizes all tier-based feature limitations.
 * Free tier: 1 pipeline, 3 sessions, basic templates only
 * Starter tier: curated templates, 1 pipeline, 5 invoices/month, simple runway
 * Pro tier: unlimited
 */
(function () {
  'use strict';

  // Tier constants
  const TIERS = {
    FREE: 'free',
    STARTER: 'starter',
    PRO: 'pro'
  };

  // Feature limits per tier
  const LIMITS = {
    [TIERS.FREE]: {
      maxPipelines: 1,
      maxSessions: 3,
      maxInvoicesPerMonth: 0, // Free can't create invoices
      templates: 'basic', // basic templates only
      runway: 'none', // no runway access
      dailyOps: false, // no daily ops
      missionMemory: false, // no mission memory
      canExport: false, // no export
      canShare: false // no sharing
    },
    [TIERS.STARTER]: {
      maxPipelines: 1,
      maxSessions: 10, // Starter gets more sessions
      maxInvoicesPerMonth: 5,
      templates: 'curated', // curated templates pack
      runway: 'simple', // simple runway only
      dailyOps: false,
      missionMemory: false,
      canExport: true,
      canShare: false
    },
    [TIERS.PRO]: {
      maxPipelines: Infinity,
      maxSessions: Infinity,
      maxInvoicesPerMonth: Infinity,
      templates: 'all',
      runway: 'full',
      dailyOps: true,
      missionMemory: true,
      canExport: true,
      canShare: true
    }
  };

  /**
   * Get current tier from license
   * @returns {string} 'free', 'starter', or 'pro'
   */
  function getTier() {
    const state = window.IndxLicense?.getState?.();
    return state?.tier || TIERS.FREE;
  }

  /**
   * Check if current user has Pro license
   * @returns {boolean}
   */
  function isPro() {
    return getTier() === TIERS.PRO;
  }

  /**
   * Check if current user has Starter license
   * @returns {boolean}
   */
  function isStarter() {
    return getTier() === TIERS.STARTER;
  }

  /**
   * Check if current user has Free tier
   * @returns {boolean}
   */
  function isFree() {
    return getTier() === TIERS.FREE;
  }

  /**
   * Get limits for current tier
   * @returns {Object}
   */
  function getLimits() {
    return LIMITS[getTier()] || LIMITS[TIERS.FREE];
  }

  /**
   * Check if user can add more pipelines
   * @param {number} currentCount - Current number of pipelines/missions
   * @returns {boolean}
   */
  function canAddPipeline(currentCount) {
    const limits = getLimits();
    return currentCount < limits.maxPipelines;
  }

  /**
   * Check if user can add more sessions
   * @param {number} currentCount - Current number of sessions
   * @returns {boolean}
   */
  function canAddSession(currentCount) {
    const limits = getLimits();
    return currentCount < limits.maxSessions;
  }

  /**
   * Check if user can create more invoices this month
   * @param {number} currentMonthCount - Invoices created this month
   * @returns {boolean}
   */
  function canCreateInvoice(currentMonthCount) {
    const limits = getLimits();
    return currentMonthCount < limits.maxInvoicesPerMonth;
  }

  /**
   * Check if user has access to advanced templates
   * @returns {boolean}
   */
  function hasAdvancedTemplates() {
    const limits = getLimits();
    return limits.templates !== 'basic';
  }

  /**
   * Check if user has access to the full template catalog
   * @returns {boolean}
   */
  function hasAllTemplates() {
    const limits = getLimits();
    return limits.templates === 'all';
  }

  /**
   * Check if user has access to full runway
   * @returns {boolean}
   */
  function hasFullRunway() {
    const limits = getLimits();
    return limits.runway === 'full';
  }

  /**
   * Check if user has access to daily ops
   * @returns {boolean}
   */
  function hasDailyOps() {
    const limits = getLimits();
    return limits.dailyOps;
  }

  /**
   * Check if user has access to mission memory
   * @returns {boolean}
   */
  function hasMissionMemory() {
    const limits = getLimits();
    return limits.missionMemory;
  }

  /**
   * Check if user can export
   * @returns {boolean}
   */
  function canExport() {
    const limits = getLimits();
    return limits.canExport;
  }

  /**
   * Check if user can share
   * @returns {boolean}
   */
  function canShare() {
    const limits = getLimits();
    return limits.canShare;
  }

  // Public API
  window.IndxLimits = {
    TIERS,
    LIMITS,
    getTier,
    isPro,
    isStarter,
    isFree,
    getLimits,
    canAddPipeline,
    canAddSession,
    canCreateInvoice,
    hasAdvancedTemplates,
    hasAllTemplates,
    hasFullRunway,
    hasDailyOps,
    hasMissionMemory,
    canExport,
    canShare
  };
})();
